import { useEffect, useRef, useState } from "react";
import { useShell } from "../Layout";
import { Elapsed, since, Spin, STAGE_WORDS, useLive } from "../live";
import { getRuns, reprocessRun, type ApiError, type LiveRun, type RunRow } from "../api";
import { tracked, useAsync } from "../useAsync";
import { treeHref } from "./Submission";
import {
  Badge, Card, CardBody, CardHead, DASH, Empty, ErrorBox, Freshness, Kpi, NearMiss,
  Pane, ScopeBar, sorted, State, Th, When, type Sort,
} from "../ui";
import { RunLogDialog } from "../runlog";
import { UsageCell } from "../usage";

export function RunsTab() {
  const { courseId, access, overview } = useShell();
  // 🔴 THE WIDE READ, ALWAYS — the server bounds it to the cohorts this teacher may view.
  // The filter is presentation, because the near-miss row has to say what is being HIDDEN and
  // that is not knowable from a request that already dropped it.
  const q = useAsync(() => getRuns(courseId, "all"), [courseId]);
  // 🔴 A RUN THAT ENDS IS READ AT ONCE (2026-10-06), and one in flight says its stage and time.
  // The feed is this cohort's, so another cohort's rows keep the word the list last read.
  const live = useLive();
  const inFlight = new Map((live.feed?.runs ?? []).map((r) => [r.attempt_id, r]));
  const lastEvent = useRef(0);
  const reloadRuns = q.reload;
  useEffect(() => {
    const newest = live.events[live.events.length - 1]?.n ?? 0;
    if (newest > lastEvent.current) { lastEvent.current = newest; reloadRuns(); }
  }, [live.events, reloadRuns]);
  const [sort, setSort] = useState<Sort>({ key: "started_at", dir: -1 });
  const [outcome, setOutcome] = useState<string>("");
  // ⚠️ ARRIVED-FROM, NOT CHOSEN. You reached this page from inside a cohort, so it starts
  // narrowed to it — and says so, because a filter you inherited by navigating is the one
  // that needs explaining.
  const [mine, setMine] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [err, setErr] = useState<ApiError | null>(null);
  const [logOf, setLogOf] = useState<RunRow | null>(null);

  // 🔴 IT STARTS NOTHING. The route hands the stored envelope back to the collector; the page
  // and the sidebar reload because a held run is no longer one that stored nothing.
  const reprocess = (r: RunRow) => {
    setBusy(r.id); setErr(null); setNote("");
    tracked(reprocessRun(r.course_id, r.id))
      .then((res) => setNote(res.note))
      .catch((e) => setErr(e as ApiError))
      .finally(() => { setBusy(null); q.reload(); overview?.reload(); });
  };

  const key = (r: RunRow) => ({
    started_at: r.started_at, submission: r.submission_id, engine: r.engine,
    outcome: r.outcome, points: r.points, usage: r.usage.duration_ms,
  }[sort.key] as string | number | null | undefined);

  return (
    <div className="stack">
      <div className="counts">
        <Kpi label="Attempts" value={q.data?.counts.total}
             hint="every grading job this console started, whatever became of it" />
        <Kpi label="Running" value={q.data?.counts.running} tone="held"
             hint="started and not yet collected — polling, not lost" />
        <Kpi label="Recorded" value={q.data?.counts.recorded} tone="ok"
             hint="finished and stored a grade you can read" />
        <Kpi label="Stored nothing" value={q.data?.counts.paid_and_stored_nothing} tone="stop"
             hint="paid for and produced no row — the case this page exists for" />
      </div>
      {/* 🔴 FOUR OF THE EIGHT COUNTS WERE ON THE WIRE AND RENDERED NOWHERE. `refused`,
          `failed`, `abandoned` and `superseded` were computed on every request and dropped —
          and the first two are the pair a teacher most needs kept apart, because a refusal is
          a decision somebody made and a failure is something broken. */}
      <div className="counts">
        <Kpi label="Refused" value={q.data?.counts.refused} tone="warn"
             hint="a decision, not a fault — the sentence names the remedy and nothing was spent" />
        <Kpi label="Failed" value={q.data?.counts.failed} tone="stop"
             hint="infrastructure, not the student — no releasable mark was produced" />
        <Kpi label="Abandoned" value={q.data?.counts.abandoned} tone="stop"
             hint="gave up waiting past the in-flight limit; the job was cancelled" />
        <Kpi label="Superseded" value={q.data?.counts.superseded}
             hint="stored a grade a later run replaced — a second opinion, nothing lost" />
        {/* 🔴 A USAGE LIMIT IS NOT A LOST RUN. Counted apart from "Stored nothing" so that
            alarm keeps meaning something on a subscription credential, where limits are the
            ordinary state of a busy afternoon. */}
        <Kpi label="Limited" value={q.data?.counts.limited} tone="warn"
             hint="the credential's usage limit stopped it — not counted against the submission; it runs again after the reset" />
        {/* ⚠️ NOR IS A HELD RUN: paid for, its envelope stored, and finalised again by itself. */}
        <Kpi label="Held" value={q.data?.counts.held} tone="held"
             hint="finished and paid for; finalising failed for a reason of ours and is tried again by itself — nothing is paid for again" />
      </div>
      {err && <ErrorBox err={err} />}
      {note && <p className="alert small" role="status">{note}</p>}

      <Card>
        <CardHead
          title="Runs"
          desc={<>🔴 <strong>This tab exists for the rows that are nowhere else.</strong> An
                infrastructure refusal stores nothing by design, so under Windmill it existed
                only as a job and vanished when history rolled off at 30 days. These rows are
                in our own database and never expire.</>}
          right={<Freshness q={q} />} />
        <ScopeBar name={access.name} on={mine} count={q.data?.rows.length ?? 0}
                  onChange={setMine} />
        <div className="log-filters">
          <button className={outcome ? "chip" : "chip on"} aria-pressed={!outcome}
                  onClick={() => setOutcome("")}>all</button>
          {(["running", "held", "recorded", "refused", "failed", "abandoned", "limited"] as const).map((o) => (
            <button key={o} className={outcome === o ? "chip on" : "chip"}
                    aria-pressed={outcome === o}
                    onClick={() => setOutcome(outcome === o ? "" : o)}>
              {o}<span className="nav-count">{q.data?.counts[o] ?? 0}</span>
            </button>))}
          <button className={outcome === "stored_nothing" ? "chip on" : "chip"}
                  aria-pressed={outcome === "stored_nothing"}
                  title="Finished and produced no row. It still counts against the runs-per-day
backstop, and what it used of the subscription was used."
                  onClick={() => setOutcome(outcome === "stored_nothing"
                                            ? "" : "stored_nothing")}>
            stored nothing
            <span className="nav-count stop">
              {q.data?.counts.paid_and_stored_nothing ?? 0}</span>
          </button>
        </div>
        <CardBody tight>
          <Pane q={q} empty={<Empty title="No grading runs yet"
                                    hint="A run appears the moment a job is dispatched, before
                                          it produces anything." />}>
            {(d) => {
              const rows = d.rows.filter((r) =>
                !outcome
                || (outcome === "stored_nothing"
                    // ⚠️ THE SERVER'S RULE, NOT A SECOND ONE: `runs.counts` excludes a limited
                    // run from "paid and stored nothing", so this filter must too.
                    ? r.stored_nothing
                    : r.outcome === outcome));
              // Everything the OTHER filters accept, split by the cohort one. The near-misses
              // are rendered dimmed rather than dropped.
              const here = rows.filter((r) => r.course_id === courseId);
              const away = rows.filter((r) => r.course_id !== courseId);
              const shown = mine ? [...here, ...away] : rows;
              return (
              <div className="tablewrap">
                <table className="tb">
                  <thead><tr>
                    <Th label="Started" k="started_at" sort={sort} setSort={setSort} />
                    <Th label="Submission" k="submission" sort={sort} setSort={setSort} />
                    <Th label="Engine" k="engine" sort={sort} setSort={setSort} />
                    <Th label="Outcome" k="outcome" sort={sort} setSort={setSort} />
                    <Th label="Mark" k="points" sort={sort} setSort={setSort} right />
                    {/* 🔴 USAGE, NOT A PRICE (owner, 2026-10-03): time first, then tokens and
                        how far the weekly figure moved while it ran — from the run's OWN envelope,
                        so a run that stored nothing or was superseded shows what it used. */}
                    <Th label="Usage" k="usage" sort={sort} setSort={setSort} right
                        title="How long the run took, the tokens it used, and how far the subscription’s weekly usage moved while it ran — your own Claude use in the same minutes counts too." />
                    <th className="wrap">Detail</th>
                  </tr></thead>
                  <tbody>
                    {sorted(shown, key, sort.dir).map((r) => (
                      <tr key={r.id}
                          className={mine && r.course_id !== courseId ? "faint" : undefined}>
                        <td><When iso={r.started_at} /></td>
                        <td>
                          <code>{r.submission_id.slice(-10)}</code>
                          {/* ⚠️ THE COMMIT, NOT THE BRANCH: what this run actually read. */}
                          {r.commit_sha &&
                            <div><a className="small" target="_blank" rel="noreferrer"
                                    title={`commit ${r.commit_sha}`}
                                    href={treeHref({ url: r.repo_url, ref: "",
                                                     subpath: r.repo_subpath,
                                                     commit: r.commit_sha })}>
                              <code>{r.commit_sha.slice(0, 7)}</code></a></div>}
                          {r.attempt_no > 1 && <> <Badge tone="warn" title="🔴 MAX_ATTEMPTS is 2.
A job that pays and stores nothing never reaches the store, so it never leaves the queue: it
ages past the in-flight limit and is run again every 90 minutes. The runs-per-day backstop
counts it, and the count still stops it at two.">try {r.attempt_no}</Badge></>}
                        </td>
                        <td><code className="small">{r.engine}</code></td>
                        <td>
                          <RunState outcome={r.outcome} now={inFlight.get(r.id)} />
                          {/* 🔴 THE ROW THIS TAB EXISTS FOR. "Succeeded, stored nothing" is
                              the worst case this system has a word for. */}
                          {r.stored_nothing &&
                            <> <Badge tone="stop">stored nothing</Badge></>}
                          {/* ⚠️ SUPERSEDED IS NOT LOST. This attempt DID store; a later
                              re-run replaced the row. A second opinion is a designed
                              workflow, and calling it "stored nothing" turned the alarm for
                              a lost paid run into noise on healthy work. */}
                          {r.superseded &&
                            <> <Badge tone="plain" title="This attempt stored a grade; a later
run for the same submission replaced it. Nothing was lost.">superseded</Badge></>}
                        </td>
                        <td className="r num">{r.points ?? <span className="muted">{DASH}</span>}</td>
                        <td className="r use-cell">
                          <UsageCell ms={r.usage.duration_ms} tokens={r.usage.tokens_total}
                                     weekly={r.usage.weekly} gateway={r.usage.gateway}
                                     showWeekly /></td>
                        <td className="wrap small">
                          {/* 🔴 IN FULL. This was truncated at 110 characters with the rest
                              in a `title` — on the one tab that exists for failed runs. Every
                              refusal in this system is written to name the REMEDY, and the
                              remedy is always in the second half of the sentence. A tooltip
                              is invisible on a screenshot, on touch, and to a screen reader. */}
                          {r.error_message
                            ? <span>{r.error_message}</span>
                            : r.envelope_received_at
                              ? <span className="muted" title={`envelope committed ${
                                  r.envelope_received_at}, processed ${r.processed_at ?? "not yet"}
— durable-accept: the paid envelope is committed before anything is validated, so a crash
re-processes from the database instead of re-running the agent.`}>accepted, then processed</span>
                              : <span className="muted">{DASH}</span>}
                          {r.outcome === "held" && r.retry_finalise_at &&
                            <div className="muted">finalised again by itself{" "}
                              <When iso={r.retry_finalise_at} /></div>}
                          {/* ⚠️ ONLY ON THIS COHORT'S ROWS. The capability is per cohort and the
                              page knows it for this one; a dimmed row from another is for
                              reading. The reason sits beside a disabled button, not in a
                              tooltip. */}
                          {r.reprocessable && r.course_id === courseId &&
                            <div>
                              <button className="btn sm"
                                      disabled={!access.can_run_grading || busy != null}
                                      onClick={() => reprocess(r)}>
                                {busy === r.id ? "Reprocessing…" : "Reprocess"}</button>
                              {!access.can_run_grading &&
                                <span className="muted"> needs Run grading on this cohort</span>}
                            </div>}
                          {/* 🔴 WHAT IT DID, STEP BY STEP (2026-10-08), for any run whose envelope
                              arrived: a run that stored nothing has no report to carry its log.
                              Asked under the run's own cohort, which the server checks. */}
                          {r.envelope_received_at &&
                            <div><button className="btn ghost sm" type="button"
                                         onClick={() => setLogOf(r)}>Run log</button></div>}
                        </td>
                      </tr>))}
                  </tbody>
                </table>
                {rows.length !== d.rows.length && (
                  <p className="small muted" style={{ padding: "10px 12px" }}>
                    {rows.length} of {d.rows.length} shown by the filter.</p>)}
                {mine && <NearMiss n={away.length} onInclude={() => setMine(false)} />}
              </div>); }}
          </Pane>
        </CardBody>
      </Card>
      {logOf && <RunLogDialog courseId={logOf.course_id} attemptId={logOf.id}
                              onClose={() => setLogOf(null)} />}
    </div>
  );
}

/** A run's state: its stage and time while the feed says it is in flight, else the word the list
 *  last read. ⚠️ A held run carries no timer — what it waits for is the retry, not the run. */
function RunState({ outcome, now }: { outcome: string; now?: LiveRun }) {
  if (!now) return <State s={outcome} />;
  return (
    <span className="rc-state">
      <Spin dim={now.stage === "starting" || now.stage === "held"} />
      {STAGE_WORDS[now.stage]}
      {now.stage !== "starting" && now.stage !== "held" &&
        <>{" "}<Elapsed since={since(now)} /></>}
    </span>);
}
